import type { SessionPositioning } from '~/gql/positioning/positioning'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table'
import { levelGap } from './level-gap'
import ProfileSection from './ProfileSection'

const NONE = '—'

/**
 * Le positionnement de l'apprenant (#321), parcours par parcours : son niveau
 * sur chaque objectif à l'entrée et à la sortie, l'écart, et les attentes
 * exprimées au départ (Qualiopi, indicateurs 4, 8 et 11).
 */
function ProfilePositioning({ sessions }: { sessions: readonly SessionPositioning[] }) {
  const { t } = useTranslation()

  return (
    <ProfileSection title={t('profile.positioning.title')} anchor="profile.positioning">
      {sessions.length === 0
        ? <p className="text-sm text-muted-foreground">{t('profile.positioning.empty')}</p>
        : (
            <div className="flex flex-col gap-6">
              {sessions.map(s => (
                <div key={s.sessionId} className="flex flex-col gap-2">
                  <h3 className="font-medium">{s.sessionName}</h3>
                  {!s.entry
                    ? <p className="text-sm text-muted-foreground">{t('profile.positioning.awaiting')}</p>
                    : (
                        <>
                          <div className="overflow-x-auto rounded-md border">
                            <Table aria-label={t('profile.positioning.tableFor', { name: s.sessionName })}>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="min-w-48 whitespace-normal">{t('profile.positioning.objective')}</TableHead>
                                  <TableHead>{t('profile.positioning.entry')}</TableHead>
                                  <TableHead>{t('profile.positioning.exit')}</TableHead>
                                  <TableHead>{t('profile.positioning.gap')}</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {s.entry.ratings.map((rating, index) => {
                                  const exit = s.exit?.ratings[index]?.level
                                  return (
                                    <TableRow key={rating.objective}>
                                      <TableCell className="min-w-48 whitespace-normal">{rating.objective}</TableCell>
                                      <TableCell className="tabular-nums">{`${rating.level} / 5`}</TableCell>
                                      <TableCell className="tabular-nums">{exit != null ? `${exit} / 5` : NONE}</TableCell>
                                      <TableCell className="tabular-nums">{levelGap(rating.level, exit)}</TableCell>
                                    </TableRow>
                                  )
                                })}
                              </TableBody>
                            </Table>
                          </div>
                          {s.entry.needs && (
                            <p className="text-sm">
                              <span className="text-muted-foreground">{t('profile.positioning.needs')}</span>
                              {' '}
                              {s.entry.needs}
                            </p>
                          )}
                        </>
                      )}
                </div>
              ))}
            </div>
          )}
    </ProfileSection>
  )
}

export default ProfilePositioning
